const MEMORY_CACHE = new Map();
const IN_FLIGHT_REQUESTS = new Map();
const CACHE_PREFIX = "rsps:cache:";

function getStorageKey(key) {
  return `${CACHE_PREFIX}${key}`;
}

function isBrowserStorageAvailable() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readFromStorage(key) {
  if (!isBrowserStorageAvailable()) return null;

  try {
    const raw = window.localStorage.getItem(getStorageKey(key));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function writeToStorage(key, entry) {
  if (!isBrowserStorageAvailable()) return;

  try {
    window.localStorage.setItem(getStorageKey(key), JSON.stringify(entry));
  } catch (error) {
    // Ignore storage quota and serialization errors.
  }
}

function removeFromStorage(key) {
  if (!isBrowserStorageAvailable()) return;

  try {
    window.localStorage.removeItem(getStorageKey(key));
  } catch (error) {
    // Ignore storage access errors.
  }
}

function isFreshEntry(entry) {
  if (!entry || typeof entry !== "object") return false;

  const { cachedAt, ttlMs } = entry;
  if (typeof cachedAt !== "number" || typeof ttlMs !== "number") return false;

  return Date.now() - cachedAt <= ttlMs;
}

function getMemoryEntry(key) {
  const entry = MEMORY_CACHE.get(key);
  if (!entry) return null;

  if (!isFreshEntry(entry)) {
    MEMORY_CACHE.delete(key);
    return null;
  }

  return entry;
}

function getStorageEntry(key) {
  const entry = readFromStorage(key);
  if (!entry) return null;

  if (!isFreshEntry(entry)) {
    removeFromStorage(key);
    return null;
  }

  return entry;
}

export function getCachedValue(key) {
  const memoryEntry = getMemoryEntry(key);
  if (memoryEntry) return memoryEntry.value;

  const storageEntry = getStorageEntry(key);
  if (!storageEntry) return null;

  MEMORY_CACHE.set(key, storageEntry);
  return storageEntry.value;
}

export function setCachedValue(key, value, ttlMs) {
  const entry = {
    value,
    cachedAt: Date.now(),
    ttlMs,
  };

  MEMORY_CACHE.set(key, entry);
  writeToStorage(key, entry);
}

export function invalidateCache(key) {
  MEMORY_CACHE.delete(key);
  removeFromStorage(key);
}

export function invalidateCacheByPrefix(prefix) {
  const keys = [...MEMORY_CACHE.keys()];
  keys.forEach((key) => {
    if (key.startsWith(prefix)) {
      MEMORY_CACHE.delete(key);
    }
  });

  if (!isBrowserStorageAvailable()) return;

  try {
    const fullPrefix = getStorageKey(prefix);
    const removals = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const storageKey = window.localStorage.key(i);
      if (storageKey && storageKey.startsWith(fullPrefix)) {
        removals.push(storageKey);
      }
    }
    removals.forEach((storageKey) => window.localStorage.removeItem(storageKey));
  } catch (error) {
    // Ignore storage access errors.
  }
}

export async function getOrSetCache(key, fetcher, ttlMs = 60000) {
  const cachedValue = getCachedValue(key);
  if (cachedValue !== null) return cachedValue;

  if (IN_FLIGHT_REQUESTS.has(key)) {
    return IN_FLIGHT_REQUESTS.get(key);
  }

  const requestPromise = (async () => {
    const value = await fetcher();
    setCachedValue(key, value, ttlMs);
    return value;
  })();

  IN_FLIGHT_REQUESTS.set(key, requestPromise);

  try {
    return await requestPromise;
  } finally {
    IN_FLIGHT_REQUESTS.delete(key);
  }
}
